import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionTitle from '~/components/section-title/index';
import { useAtomValue } from 'jotai';
import { useViewTracking } from '~/utils/use-view-tracking';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ModuleType } from '~/utils/module-type';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPositionPrefix } from '~/utils/click-position';
import { Masonry, MasonryRef } from '~/components/masonry';
import cls from 'classnames';
import { $CurrentExperienceRelatedPost } from '../../shared/atoms/current-experience-related-data';
import ArticleRecommendCardV3 from '~/components/article-recommend-card-v3';
import { DetailPostModal } from '../related-post-detail-modal';
import { EArticleClass } from 'shared/models';
import { $CurrentExperienceAnswerAsAvailable } from '../../shared/atoms';
import { ArticleRecommendSkeleton } from '~/skeleton/article';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { DivisibleStack } from '@lemon8/web-app-shared/component';
import { ArticleCard } from '~/components/article-card';

import './style.scss';

export const ExperienceRelatedPostWithClickableDetail: FC = () => {
  const { t } = useTranslation();

  const [loadingStyle, setLoading] = useState({
    display: 'block',
  });
  const [containerStyle, setContainerStyle] = useState({
    opacity: 0,
  });
  const relatedPosts = useAtomValue($CurrentExperienceRelatedPost);
  const { articleInfoList } = useAtomValue($CurrentExperienceAnswerAsAvailable);
  const { isBot } = useAtomValue($UserAgent);

  const answerUsedGroupIds = useMemo(() => {
    return articleInfoList.map((item) => item.groupId);
  }, [articleInfoList]);

  const filteredRelatedPosts = useMemo(() => {
    return relatedPosts.items.filter(
      (item) => item.articleClass !== EArticleClass.ARTICLE && !answerUsedGroupIds.includes(item.groupId),
    );
  }, [relatedPosts]);

  const sectionObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.experience_related_post,
        related_posts_count: filteredRelatedPosts.length,
      });
    }, []),
  });

  const masonryRef = useRef<MasonryRef>(null);
  const masonryFirstRenderRef = useRef<boolean>(true);

  useEffect(() => {
    if (masonryRef.current && masonryFirstRenderRef.current) {
      masonryFirstRenderRef.current = false;
      masonryRef.current.postLayout(() => {
        setLoading({
          display: 'none',
        });
        setContainerStyle({
          opacity: 1,
        });
      });
    }
  }, [filteredRelatedPosts, masonryRef.current]);

  if (relatedPosts.items.length === 0) {
    return null;
  }

  return (
    <section
      id="experience-article-related-post"
      ref={sectionObservedRef}
      className={cls('experience-article-recommend-post', { darkBackground: true })}
    >
      <div className="experience-recommend-title">
        <SectionTitle title={t('wap_seo_recoomend_post')} />
      </div>
      <>
        <ScopedProvider
          atomsWithHydration={[
            [$CurrentPageModule, ModuleType.experience_related_post],
            [$CurrentPositionPrefix, ClickPositionPrefix.none],
          ]}
        >
          {isBot ? (
            <>
              <DivisibleStack divider="article-related-post-divider">
                {filteredRelatedPosts.map((item, rank) => (
                  <ArticleCard key={item.groupId} article={item} rank={rank} />
                ))}
              </DivisibleStack>
            </>
          ) : (
            <>
              <div className="experience-recommend-loading" style={loadingStyle}>
                <ArticleRecommendSkeleton />
              </div>
              <div className="experience-recommend-masonry-container" style={containerStyle}>
                <Masonry ref={masonryRef} columnCount={2} className="recommend-masonry">
                  {filteredRelatedPosts.map((item, index) => (
                    <ArticleRecommendCardV3 key={item.groupId} article={item} rank={index} />
                  ))}
                </Masonry>
              </div>
              <DetailPostModal />
            </>
          )}
        </ScopedProvider>
      </>
    </section>
  );
};
